/**
 * Organizr.tsx - Organizr - Universal list and category management UI
 * 
 * CHANGE HISTORY:
 * - 251021_0438 [46df53c] danielbeer: feat: complete comprehensive package migration to full standards
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useOrganizr } from './OrganizrContext';
import ListEditor from '../components/ListEditor.jsx';
import ListSidebar from '../components/ListSidebar.jsx';
import ImportExportModal from '../components/ImportExportModal.jsx';
import IconGallery from '../components/IconGallery.jsx';
// Legacy CSS removed - now using Skinnr

const Organizr = () => {
  const {
    getAllLists,
    createList,
    deleteList,
    undo,
    redo,
    canUndo,
    canRedo
  } = useOrganizr();
  
  const [selectedListId, setSelectedListId] = useState(null);
  const [showImportExport, setShowImportExport] = useState(false);
  const [showIconGallery, setShowIconGallery] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const lists = getAllLists();
  const selectedList = lists.find(l => l.id === selectedListId);
  
  // Select first list by default
  useEffect(() => {
    if (!selectedListId && lists.length > 0) {
      setSelectedListId(lists[0].id);
    }
  }, [lists, selectedListId]);
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;
      
      // Cmd+N: New list
      if (cmdOrCtrl && e.key === 'n') {
        e.preventDefault();
        handleCreateList();
      }
      
      // Cmd+Z: Undo
      if (cmdOrCtrl && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndo()) undo();
      }
      
      // Cmd+Shift+Z or Cmd+Y: Redo
      if (cmdOrCtrl && ((e.key === 'z' && e.shiftKey) || e.key === 'y')) {
        e.preventDefault();
        if (canRedo()) redo();
      }
      
      // Cmd+I: Import/Export
      if (cmdOrCtrl && e.key === 'i') {
        e.preventDefault();
        setShowImportExport(true);
      }
      
      // Cmd+K: Icon gallery
      if (cmdOrCtrl && e.key === 'k') {
        e.preventDefault();
        setShowIconGallery(true);
      }
      
      // Delete: Delete selected list
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedListId && e.target.tagName !== 'INPUT') {
        e.preventDefault();
        handleDeleteList(selectedListId);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedListId, canUndo, canRedo, undo, redo]);
  
  const handleCreateList = useCallback(() => {
    const newList = createList({
      name: 'New List',
      color: 'var(--text-tertiary)',
      icon: '📋'
    });
    setSelectedListId(newList.id);
  }, [createList]);
  
  const handleDeleteList = useCallback((listId) => {
    if (window.confirm('Are you sure you want to delete this list?')) {
      deleteList(listId);
      if (selectedListId === listId) {
        const remaining = lists.filter(l => l.id !== listId);
        setSelectedListId(remaining[0]?.id || null);
      }
    }
  }, [deleteList, lists, selectedListId]);
  
  const handlePaste = useCallback(async (e) => {
    // Handle paste events for quick import
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
    
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    
    if (!text) return;
    
    // Parse with Parsely
    const parser = new Parsely();
    const result = parser.parse(text);
    
    if (result.data && result.data.length > 0) {
      // Create new list from pasted data
      const listName = `Pasted List (${new Date().toLocaleTimeString()})`;
      const items = result.data.map((item, index) => ({
        name: typeof item === 'string' ? item : (item.text || item.name || JSON.stringify(item)),
        value: typeof item === 'string' ? item.toLowerCase().replace(/\s+/g, '-') : item.value,
        order: index
      }));
      
      const newList = createList({
        name: listName,
        children: items
      });
      
      setSelectedListId(newList.id);
    }
  }, [createList]);
  
  // Add paste listener
  useEffect(() => {
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [handlePaste]);
  
  // Filter lists by search
  const filteredLists = searchQuery
    ? lists.filter(list => 
        list.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : lists;
  
  return (
    <div className="organizr">
      {/* Sidebar */}
      <ListSidebar
        lists={filteredLists}
        selectedListId={selectedListId}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onSelectList={setSelectedListId}
        onCreateList={handleCreateList}
        onDeleteList={handleDeleteList}
      />
      
      {/* Main editor */}
      <div className="organizr-main">
        {selectedList ? (
          <ListEditor
            list={selectedList}
            onShowIconGallery={() => setShowIconGallery(true)}
          />
        ) : (
          <div className="organizr-empty-state">
            <h2>No list selected</h2>
            <p>Create a new list or select one from the sidebar</p>
            <button onClick={handleCreateList} className="btn-create-first-list">
              Create First List
            </button>
          </div>
        )}
      </div>
      
      {/* Toolbar */}
      <div className="organizr-toolbar">
        <div className="organizr-toolbar-left">
          <button
            onClick={() => undo()}
            disabled={!canUndo()}
            className="btn-toolbar"
            title="Undo (Cmd+Z)"
          >
            ↶
          </button>
          <button
            onClick={() => redo()}
            disabled={!canRedo()}
            className="btn-toolbar"
            title="Redo (Cmd+Shift+Z)"
          >
            ↷
          </button>
        </div>
        
        <div className="organizr-toolbar-right">
          <button
            onClick={() => setShowIconGallery(true)}
            className="btn-toolbar"
            title="Icon Gallery (Cmd+K)"
          >
            🎨
          </button>
          <button
            onClick={() => setShowImportExport(true)}
            className="btn-toolbar"
            title="Import/Export (Cmd+I)"
          >
            ⤓⤒
          </button>
        </div>
      </div>
      
      {/* Modals */}
      {showImportExport && (
        <ImportExportModal
          selectedListId={selectedListId}
          onClose={() => setShowImportExport(false)}
        />
      )}
      
      {showIconGallery && (
        <IconGallery
          onClose={() => setShowIconGallery(false)}
          onSelect={(icon) => {
            // Icon selected - would update current selection
            console.log('Selected icon:', icon);
            setShowIconGallery(false);
          }}
        />
      )}
    </div>
  );
};

export default Organizr;
